import React, { useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { API, ViewHeader, Card, Help, Input, Select, Submit, PageLoader, TextArea, Date as FormDate } from '@enkla-gui'
import { useGet } from '@enkla-gui'
import { date, notify } from '@/_helpers'

export default function CreateQuittance() {
  const { id: leaseId } = useParams()
  const history = useHistory()
  const { data: lease, loading: leaseLoading } = useGet({ endpoint: `api/lease/${leaseId}` })
  const [sending, setSending] = useState(false)

  const { register, watch, setValue, handleSubmit, control } = useForm({
    defaultValues: {
      items: [newItem()]
    }
  })

  function newItem() {
    return {
      _tmpId: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: '',
      quantity: 1,
      type: '',
      marking: '',
      description: '',
      handout_date: date.now()
    }
  }

  const items = watch('items') || []

  const addRow = () => {
    setValue('items', [...items, newItem()])
  }

  const removeRow = index => {
    if (items.length === 1) return
    setValue('items', items.filter((_, i) => i !== index))
  }

  const onSubmit = data => {
    const rows = (data.items || []).filter(i => i.name && i.type)
    if (rows.length === 0) {
      notify.toast('Fyll i minst namn och typ på en rad', 'error')
      return
    }
    setSending(true)
    Promise.all(
      rows.map(({ _tmpId, ...i }) =>
        API.post('api/quittance', { ...i, lease_id: leaseId })
      )
    )
      .then(() => {
        notify.toast(`${rows.length} kvittens${rows.length > 1 ? 'er' : ''} skapad${rows.length > 1 ? 'e' : ''}.`)
        history.push(`/lease/${leaseId}`)
      })
      .catch(([err, res]) => {
        notify.toast(res?.message || 'Kunde inte skapa kvittenser', 'error')
        setSending(false)
      })
  }

  if (leaseLoading) return <PageLoader />

  return (
    <>
      <ViewHeader
        title='Uthyrning'
        subtitle='Lägg till kvittenser'
        icon='fas fa-key'
        breadcrumbs={lease?.breadcrumb}
        buttons={[
          {
            icon: 'fa-times',
            title: 'Avbryt',
            callback: () => history.push(`/lease/${leaseId}`),
            bg: 'light'
          }
        ]}
      />
      <Card
        title='Kvittenser'
        icon='fas fa-key'
        buttons={{
          header: [{ callback: addRow, icon: 'fas fa-plus-square', text: 'Lägg till rad' }],
          footer: [
            <Submit
              onSubmit={handleSubmit(onSubmit)}
              text='Spara'
              sending={sending}
              disabled={!items.some(i => i.name && i.type) || sending}
            />
          ]
        }}
      >
        <Help type='info' icon='fas fa-info-circle'>
          Lägg till kvittenser. Endast rader med både namn och typ sparas. {items.filter(i => i.name && i.type).length}/{items.length} kompletta rader.
        </Help>
        {items.map((item, index) => {
          const incomplete = !(item.name && item.type)
          return (
            <div key={item._tmpId} className={`border rounded p-3 mb-3 ${incomplete ? 'bg-light' : ''}`}>
              <div className='d-flex justify-content-between align-items-start mb-2'>
                <strong>Rad {index + 1}</strong>
                <button
                  type='button'
                  className='btn btn-sm btn-light text-danger'
                  onClick={() => removeRow(index)}
                  disabled={items.length === 1}
                  title='Ta bort rad'
                >
                  <i className='fas fa-trash' />
                </button>
              </div>
              <div className='row g-3'>
                <Input
                  controls={{ register, setValue }}
                  name={`items.${index}.name`}
                  label='Namn'
                  placeholder='Ex. Lägenhetsnyckel'
                  cols='col-12 col-md-3'
                />
                <Input
                  controls={{ register, setValue }}
                  name={`items.${index}.quantity`}
                  type='number'
                  label='Antal'
                  options={{ valueAsNumber: true }}
                  cols='col-6 col-md-2'
                />
                <Select
                  controls={{ register, setValue }}
                  name={`items.${index}.type`}
                  label='Typ'
                  values={[
                    { key: 'Välj typ...', value: '', hidden: true },
                    { key: 'Nyckel', value: 'nyckel' },
                    { key: 'Tag/Kort', value: 'tag' },
                    { key: 'Fjärrkontroll', value: 'fjärrkontroll' },
                    { key: 'Annat', value: 'annat' }
                  ]}
                  cols='col-6 col-md-2'
                />
                <Input
                  controls={{ register, setValue }}
                  name={`items.${index}.marking`}
                  label='Märkning'
                  placeholder='A1, #123'
                  cols='col-6 col-md-2'
                />
                <FormDate
                  controls={{ control }}
                  name={`items.${index}.handout_date`}
                  label='Utlämnad'
                  placeholder='Datum'
                  cols='col-6 col-md-3'
                />
                <TextArea
                  controls={{ register, setValue }}
                  name={`items.${index}.description`}
                  label='Beskrivning'
                  rows='2'
                  cols='col-12'
                />
              </div>
            </div>
          )
        })}
      </Card>
    </>
  )
}